import { useEffect, useState } from "react";
import { Check, Loader2, Users, MonitorOff, ChevronLeft, ChevronRight } from "lucide-react";
import { LEARN_SLIDES } from "@/lib/learnSlides";

const isPuzzle = (b) => ["sudoku", "wordsearch", "maze", "puzzle", "coloring", "activity"].includes(b.mode);

export const countDone = (b) => {
  const chs = b.chapters || [];
  const done = chs.filter((c) => (c.content && c.content.trim()) || c.puzzle || c.image_id).length;
  return { done, total: Math.max(chs.length, b.num_chapters || 0) };
};

const stageIndex = (b) => {
  if (b.status === "done") return 4;
  if (b.queue?.state === "waiting") return 0;
  const { done, total } = countDone(b);
  if (b.status === "writing") return total && done >= total ? 3 : 2;
  if (["researching", "outlining"].includes(b.status)) return 1;
  return b.queue?.state === "running" ? 1 : 0;
};

export const QueueCard = ({ book }) => {
  const q = book.queue;
  if (!q || q.state !== "waiting") return null;
  return (
    <div data-testid="creation-queue-card" className="max-w-2xl mx-auto rounded-2xl border border-gold-400/25 bg-navy-900/50 p-5 flex items-center gap-4">
      <Users className="w-6 h-6 text-gold-300 shrink-0" />
      <div>
        <p className="font-display text-xl text-slate-100" data-testid="creation-queue-position">You're #{q.position} in line</p>
        <p className="font-ui text-sm text-slate-400" data-testid="creation-queue-ahead">
          {q.waiting_ahead ? `${q.waiting_ahead} waiting ahead of yours` : "Yours is next in line"} · {q.running} being written now
        </p>
      </div>
    </div>
  );
};

export const Milestones = ({ book }) => {
  const i = stageIndex(book);
  const { done, total } = countDone(book);
  const unit = isPuzzle(book) ? "puzzles" : "chapters";
  const steps = ["Waiting in line", "Planning your book", total ? `Writing · ${done} of ${total} ${unit}` : "Writing", "Cover & final checks", "Ready"];
  return (
    <ol data-testid="creation-milestones" className="max-w-2xl mx-auto grid grid-cols-5 gap-2">
      {steps.map((s, k) => (
        <li key={s} data-testid={`creation-milestone-${k}`} data-state={k < i ? "done" : k === i ? "active" : "todo"}
          className={`rounded-xl border px-2 py-3 text-center transition-colors ${k === i ? "border-gold-400 bg-gold-400/10" : k < i ? "border-emerald-400/30" : "border-gold-500/10"}`}>
          <span className="flex justify-center mb-1">
            {k < i ? <Check className="w-4 h-4 text-emerald-300" /> : k === i && k < 4 ? <Loader2 className="w-4 h-4 text-gold-300 animate-spin" /> : <span className="w-4 h-4 rounded-full border border-slate-600" />}
          </span>
          <span className={`font-ui text-[11px] leading-tight ${k === i ? "text-gold-200" : "text-slate-400"}`}>{s}</span>
        </li>
      ))}
    </ol>
  );
};

export const CloseBrowserNote = () => (
  <p data-testid="creation-close-note" className="max-w-2xl mx-auto flex items-start gap-2 font-ui text-sm text-slate-400">
    <MonitorOff className="w-4 h-4 mt-0.5 text-gold-300 shrink-0" />
    You can close this page. Your book keeps being made on our servers and is saved as it goes; it will be waiting in My Library, and we'll email you when it's ready.
  </p>
);

export const LearnSlides = () => {
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((x) => (x + 1) % LEARN_SLIDES.length), 12000);
    return () => clearInterval(t);
  }, []);
  const s = LEARN_SLIDES[i];
  return (
    <section data-testid="creation-learn" className="max-w-2xl mx-auto rounded-2xl border border-gold-500/12 bg-navy-900/40 p-6 space-y-3">
      <p className="font-ui text-[11px] uppercase tracking-widest text-gold-300">While you wait · {i + 1} of {LEARN_SLIDES.length}</p>
      <h3 data-testid="creation-learn-title" className="font-display text-2xl text-slate-100">{s.title}</h3>
      <ul className="space-y-1.5">
        {s.points.map((p) => <li key={p} className="font-read text-sm text-slate-300 leading-relaxed">• {p}</li>)}
      </ul>
      <div className="flex justify-end gap-2">
        <button data-testid="creation-learn-prev" onClick={() => setI((i - 1 + LEARN_SLIDES.length) % LEARN_SLIDES.length)} className="p-2 rounded-lg border border-gold-500/15 hover:border-gold-400/50 transition-colors"><ChevronLeft className="w-4 h-4" /></button>
        <button data-testid="creation-learn-next" onClick={() => setI((i + 1) % LEARN_SLIDES.length)} className="p-2 rounded-lg border border-gold-500/15 hover:border-gold-400/50 transition-colors"><ChevronRight className="w-4 h-4" /></button>
      </div>
    </section>
  );
};
